/**
 * Audit Log Filters Component
 * Filter controls for the audit log
 */

import { useState } from 'react'
import { Search, Filter, Calendar, X, Download } from 'lucide-react'
import type { AuditLogFilters as AuditFilters, AuditAction, AuditResourceType } from '@/types/session.types'
import { useUserStore } from '@/store/userStore'

// ============================================================================
// TYPES
// ============================================================================

export interface AuditLogFiltersProps {
  filters: AuditFilters
  onFiltersChange: (filters: Partial<AuditFilters>) => void
  onClearFilters: () => void
  onExport?: () => void
  totalCount: number
  filteredCount: number
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ACTION_OPTIONS: { value: AuditAction | 'all'; label: string; group: string }[] = [
  { value: 'all', label: 'All Actions', group: 'all' },
  // Authentication
  { value: 'login', label: 'Login', group: 'Auth' },
  { value: 'logout', label: 'Logout', group: 'Auth' },
  { value: 'login_failed', label: 'Failed Login', group: 'Auth' },
  // User Management
  { value: 'user_created', label: 'User Created', group: 'Users' },
  { value: 'user_updated', label: 'User Updated', group: 'Users' },
  { value: 'user_deleted', label: 'User Deleted', group: 'Users' },
  { value: 'user_invited', label: 'User Invited', group: 'Users' },
  // Roles
  { value: 'role_assigned', label: 'Role Assigned', group: 'Roles' },
  { value: 'role_revoked', label: 'Role Revoked', group: 'Roles' },
  // Security
  { value: 'password_changed', label: 'Password Changed', group: 'Security' },
  { value: 'password_reset', label: 'Password Reset', group: 'Security' },
  { value: 'session_terminated', label: 'Session Terminated', group: 'Security' },
  // Settings
  { value: 'settings_updated', label: 'Settings Updated', group: 'Settings' },
]

const RESOURCE_OPTIONS: { value: AuditResourceType | ''; label: string }[] = [
  { value: '', label: 'All Resources' },
  { value: 'user', label: 'User' },
  { value: 'session', label: 'Session' },
  { value: 'role', label: 'Role' },
  { value: 'settings', label: 'Settings' },
  { value: 'invitation', label: 'Invitation' },
  { value: 'tenant', label: 'Tenant' },
]

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Range' },
]

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AuditLogFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  onExport,
  totalCount,
  filteredCount,
}: AuditLogFiltersProps) {
  const [showCustomDate, setShowCustomDate] = useState(false)
  const [datePreset, setDatePreset] = useState<string>('all')
  const users = useUserStore((s) => s.users)

  // Check if any filters are active
  const hasActiveFilters =
    filters.action !== 'all' ||
    filters.resourceType ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.success !== 'all' ||
    filters.searchQuery

  // Handle date preset change
  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset)

    if (preset === 'custom') {
      setShowCustomDate(true)
      return
    }

    setShowCustomDate(false)

    const now = new Date()
    let dateFrom: string | undefined

    switch (preset) {
      case 'today':
        dateFrom = new Date(now.setHours(0, 0, 0, 0)).toISOString()
        break
      case '7days':
        dateFrom = new Date(now.setDate(now.getDate() - 7)).toISOString()
        break
      case '30days':
        dateFrom = new Date(now.setDate(now.getDate() - 30)).toISOString()
        break
      case '90days':
        dateFrom = new Date(now.setDate(now.getDate() - 90)).toISOString()
        break
      default:
        dateFrom = undefined
    }

    onFiltersChange({ dateFrom, dateTo: undefined })
  }

  return (
    <div className="space-y-4">
      {/* Main Filters Row */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            placeholder="Search by user, email, or details..."
            value={filters.searchQuery || ''}
            onChange={(e) => onFiltersChange({ searchQuery: e.target.value || undefined })}
            className="input input-bordered w-full pl-10"
          />
        </div>

        {/* Action Filter */}
        <select
          value={filters.action || 'all'}
          onChange={(e) =>
            onFiltersChange({ action: e.target.value as AuditAction | 'all' })
          }
          className="select select-bordered min-w-[160px]"
        >
          {ACTION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Resource Filter */}
        <select
          value={filters.resourceType || ''}
          onChange={(e) =>
            onFiltersChange({ resourceType: (e.target.value || undefined) as AuditResourceType | undefined })
          }
          className="select select-bordered min-w-[140px]"
        >
          {RESOURCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* User / Actor Filter */}
        <select
          value={filters.userId || ''}
          onChange={(e) =>
            onFiltersChange({ userId: e.target.value || undefined })
          }
          className="select select-bordered min-w-[150px]"
          aria-label="Filter by User"
        >
          <option value="">All Users</option>
          {users.map((u) => (
            <option key={u.userId} value={u.userId}>
              {u.fullName || u.email}
            </option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          value={String(filters.success)}
          onChange={(e) => {
            const value = e.target.value
            onFiltersChange({
              success: value === 'all' ? 'all' : value === 'true',
            })
          }}
          className="select select-bordered min-w-[120px]"
        >
          <option value="all">All Status</option>
          <option value="true">Success</option>
          <option value="false">Failed</option>
        </select>
      </div>

      {/* Date Filters Row */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-text-tertiary" />
          <span className="text-sm text-text-secondary">Time Period:</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handleDatePresetChange(preset.value)}
              className={`btn btn-sm ${
                datePreset === preset.value ? 'btn-primary' : 'btn-ghost'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Date Range */}
      {showCustomDate && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pl-6">
          <div className="flex items-center gap-2">
            <label className="text-sm text-text-secondary">From:</label>
            <input
              type="date"
              value={filters.dateFrom?.split('T')[0] || ''}
              onChange={(e) =>
                onFiltersChange({
                  dateFrom: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                })
              }
              className="input input-bordered input-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-text-secondary">To:</label>
            <input
              type="date"
              value={filters.dateTo?.split('T')[0] || ''}
              onChange={(e) =>
                onFiltersChange({
                  dateTo: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                })
              }
              className="input input-bordered input-sm"
            />
          </div>
        </div>
      )}

      {/* Active Filters & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-surface-overlay">
        <div className="flex items-center gap-4">
          {/* Filter icon */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm text-text-secondary">
              Showing {filteredCount} of {totalCount} entries
            </span>
          </div>

          {/* Active filter badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2">
              {filters.action && filters.action !== 'all' && (
                <span className="badge badge-primary gap-1">
                  Action: {filters.action.replace(/_/g, ' ')}
                  <button
                    type="button"
                    onClick={() => onFiltersChange({ action: 'all' })}
                    className="hover:text-white/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.resourceType && (
                <span className="badge badge-primary gap-1">
                  Resource: {filters.resourceType}
                  <button
                    type="button"
                    onClick={() => onFiltersChange({ resourceType: undefined })}
                    className="hover:text-white/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {filters.success !== 'all' && (
                <span className="badge badge-primary gap-1">
                  Status: {filters.success ? 'Success' : 'Failed'}
                  <button
                    type="button"
                    onClick={() => onFiltersChange({ success: 'all' })}
                    className="hover:text-white/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {(filters.dateFrom || filters.dateTo) && (
                <span className="badge badge-primary gap-1">
                  Date range
                  <button
                    type="button"
                    onClick={() => {
                      setDatePreset('all')
                      setShowCustomDate(false)
                      onFiltersChange({ dateFrom: undefined, dateTo: undefined })
                    }}
                    className="hover:text-white/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setDatePreset('all')
                setShowCustomDate(false)
                onClearFilters()
              }}
              className="btn btn-ghost btn-sm"
            >
              Clear Filters
            </button>
          )}
          {onExport && (
            <button
              type="button"
              onClick={onExport}
              className="btn btn-outline btn-sm gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
